/**
 * Print arbitrary HTML by opening it in a new window and calling print().
 *
 * This is invoked from a real user click (button onClick) so popup blockers
 * allow it. window.open + .print() is significantly more reliable than the
 * hidden-iframe approach across Chrome / Safari / Firefox — the OS-level
 * printer dialog (with printer list, paper size, copies, etc.) only shows
 * when the print is triggered on a visible window, not a 0x0 iframe.
 */
export function printHtml(html, title = 'Print') {
  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) {
    // Popup blocked — fall back to inline iframe so at least *something* prints.
    return printHtmlViaIframe(html, title);
  }
  win.document.open();
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>
      body{font-family:'Manrope', system-ui, -apple-system, sans-serif; padding:32px; color:#1A1F1A; margin:0;}
      h1{font-size:28px; margin:0 0 6px; letter-spacing:-0.015em;}
      h2{font-size:13px; margin:0; color:#7A827A; font-weight:500; letter-spacing:0.2em; text-transform:uppercase;}
      h3{font-size:16px; margin:0 0 8px;}
      .row{display:flex; justify-content:space-between; margin-bottom:24px; gap:24px;}
      table{width:100%; border-collapse:collapse; margin-top:16px;}
      th,td{border-bottom:1px solid #E8DFC8; padding:10px; text-align:left; font-size:13px;}
      th{background:#F2EBDB; font-weight:600;}
      .totals{margin-top:16px; text-align:right; font-size:14px;}
      .totals div{margin:4px 0;}
      .totals .grand{font-size:20px; font-weight:700; margin-top:8px; color:#003D80;}
      .clinic{color:#003D80; font-weight:700;}
      .muted{color:#7A827A; font-size:12px;}
      .badge{display:inline-block; padding:2px 8px; border-radius:999px; background:#F2EBDB; color:#003D80; font-size:11px; font-weight:600;}
      .footer{margin-top:48px; padding-top:12px; border-top:1px solid #E8DFC8; color:#7A827A; font-size:11px; text-align:center;}
      .grid{display:grid; grid-template-columns: 1fr 1fr; gap:8px 24px; font-size:13px;}
      .grid div strong{color:#003D80;}
      .signature-block{margin-top:48px; display:grid; grid-template-columns: 1fr 1fr; gap:32px; page-break-inside:avoid;}
      .signature-box{border-top:1px solid #1A1F1A; padding-top:6px; min-height:80px; font-size:11px; color:#7A827A; text-align:center;}
      .signature-box .label{margin-top:6px; letter-spacing:0.1em; text-transform:uppercase;}
      .stamp-box{border:1px dashed #7A827A; border-radius:8px; padding:14px; min-height:80px; display:flex; align-items:center; justify-content:center; color:#7A827A; font-size:11px; letter-spacing:0.1em; text-transform:uppercase;}
      @page { margin: 18mm; }
      @media print { .no-print { display: none !important; } }
    </style>
  </head><body>${html}<div class="no-print" style="position:fixed; bottom:16px; right:16px; background:#003D80; color:#fff; padding:10px 16px; border-radius:6px; font-size:13px; cursor:pointer;" onclick="window.print()">Print again</div></body></html>`);
  win.document.close();

  const fire = () => {
    try { win.focus(); win.print(); }
    catch (e) { console.error('print failed', e); }
  };

  // Some browsers fire load synchronously after close(), others async.
  if (win.document.readyState === 'complete') {
    setTimeout(fire, 250);
  } else {
    win.addEventListener('load', () => setTimeout(fire, 250), { once: true });
    // Safety net in case load never fires
    setTimeout(fire, 800);
  }
  return Promise.resolve();
}

/** Iframe fallback when popup is blocked. */
function printHtmlViaIframe(html, title) {
  return new Promise((resolve) => {
    const existing = document.getElementById('app-print-frame');
    if (existing) existing.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'app-print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0'; iframe.style.bottom = '0';
    iframe.style.width = '0'; iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><title>${title}</title></head><body>${html}</body></html>`);
    doc.close();
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ }
      setTimeout(() => { iframe.remove(); resolve(); }, 1500);
    }, 250);
  });
}

/** Print the entire current page. */
export function printPage() {
  window.print();
}

/**
 * Standard signature & stamp block to append at the bottom of any clinical /
 * billing document. HIPAA + GDPR + Polish/EU records require a wet signature
 * (or e-signature) and a clinic stamp on patient-facing legal documents.
 */
export function signatureBlock({ leftLabel = 'Clinician signature', rightLabel = 'Patient signature', showStamp = true } = {}) {
  return `
    <div class="signature-block">
      <div>
        <div class="signature-box"><div class="label">${leftLabel}</div></div>
      </div>
      <div>
        <div class="signature-box"><div class="label">${rightLabel}</div></div>
      </div>
    </div>
    ${showStamp ? `<div style="margin-top:24px; display:grid; grid-template-columns: 1fr 1fr; gap:32px; page-break-inside:avoid;">
      <div class="stamp-box">Clinic stamp</div>
      <div style="font-size:11px; color:#7A827A; padding-top:6px;">Date: ___________________</div>
    </div>` : ''}
  `;
}
